



























































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import PagingCtrl from './PagingCtrl.vue';
import scrollService from '@/core/scroll/scroll.service';
import urlHelper from '@/core/urlHelper.service';
import MobileNavigationAndFacets from '@/project/search/navigation/MobileNavigationAndFacets.vue';
import {
    FacetsViewModel,
    FacetViewObject,
    MenuItem,
    ProductRecommendationStockFilterType,
    ProductTileViewObject,
    SearchTranslationViewObject,
    SearchViewModel
} from '@/types/apiServerContract';
import FacetPickerCheckbox from './facets/FacetPickerCheckbox.vue';
import FacetQuickView from './facets/FacetQuickView.vue';
import localeNumberFilter from '@/project/filters/localeNumber.filter';
import { RawLocation } from 'vue-router';
import { JsonContent, ProductRecommendationsBlockViewModel } from '@/types/contentServerContract';
import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';
import { PRODUCT_SEARCH_COUNT } from './constants';
import WithSideNavigation from '@/project/layout/navigation/WithSideNavigation.vue';
import SearchProductResultList from './SearchProductResultList.vue';
import serverContextService from '@/core/serverContext.service';
import permissionService from '@/project/auth/permission.service';
import ProductRecommendationsBlock from '@/project/app/blocks/ProductRecommendationsBlock.vue';
import dictionaryService from '@/core/translation/dictionary.service';
const AdminOptions = () => import(/* webpackChunkName: 'staff-only-feature' */ './SearchAdminOptions.vue');

@Component({
    components: {
        ProductRecommendationsBlock,
        MobileNavigationAndFacets,
        PagingCtrl,
        FacetPickerCheckbox,
        FacetQuickView,
        SpinnerOverlay,
        AdminOptions,
        WithSideNavigation,
        SearchProductResultList
    }
})
export default class SearchResultPanel extends Vue {
    @Prop({ type: String, required: true })
    private contextKey!: string;

    @Prop({ type: String, default: '' })
    private term!: string;

    @Prop({ type: HTMLElement })
    private scrollContainer!: HTMLElement;

    @Prop({ type: Boolean })
    private typing!: boolean;

    @Prop({ type: String, default: '' })
    private campaignCode!: string;

    @Prop({ type: String, default: '' })
    private brandName!: string;

    @Prop({ type: String })
    private searchTitle!: string | undefined;

    @Prop({ type: Array })
    private menuItems!: MenuItem[];

    @Prop({ type: Object })
    private readonly activeMenu!: MenuItem | null;

    @Prop({ type: Object })
    private readonly rootMenuItem!: MenuItem | null;

    @Prop({ type: Array })
    private blocks!: JsonContent[];

    @Prop({ type: Array })
    private pageBlocks!: JsonContent[];

    @Prop({ type: Array })
    private pageBlocksBelow!: JsonContent[];

    @Prop({ type: Boolean, default: false })
    private showAllProducts!: boolean;

    @Prop({ type: Boolean, default: false })
    private searching!: boolean;

    @Prop({ type: Number, default: PRODUCT_SEARCH_COUNT })
    private pageSize!: number;

    @Prop({ required: true })
    private searchResult!: SearchViewModel | null;

    @Prop({ required: true })
    private facetsResult!: FacetsViewModel | null;

    @Prop({ type: Boolean, default: true })
    private enableContainer!: boolean;

    @Prop({ type: Boolean, default: false })
    private useFlattenedCategoryTree!: boolean;

    @Prop({ type: String })
    public categoryId: string | undefined;

    @Prop({ type: Boolean, default: false })
    public hideAdminOptions!: boolean;

    @Prop({ type: Boolean, default: false })
    private showBlocksAndProducts!: boolean;

    private get isExternalProductGuide() {
        return serverContextService.isExternalProductGuide;
    }

    private get hasLeftMenu():boolean {
        // default show "space" for left menu while loading, when loaded, check for menuItems.length
        return (this.searchResult === null || this.searching || this.menuItems.length > 0);
    }

    private get pageId() {
        return this.searchResult?.page ?? 1;
    }

    private get totalResults(): number {
        return this.searchResult?.totalHits ?? 0;
    }

    private get lastPage(): number {
        return this.searchResult?.lastPage ?? 0;
    }

    private get facets(): FacetViewObject[] {
        return this.facetsResult?.facets ?? [];
    }

    private get filters(): FacetViewObject[] {
        const facets: FacetViewObject[] = [];

        const filters = this.facetsResult?.filters;
        if (filters) {
            facets.push(filters);
        }

        const priceFilter = this.facetsResult?.sorting;
        if (priceFilter) {
            priceFilter.enabled = this.showPriceSortFacet;
            facets.push(priceFilter);
        }

        return facets;
    }

    get hideFacets() {
        if (!serverContextService.externalProductGuideSettings) { return false; }

        return !serverContextService.externalProductGuideSettings?.showFacets;
    }

    private get products(): ProductTileViewObject[] {
        return this.searchResult?.products ?? [];
    }

    private get showPriceSortFacet(): boolean {
        return !!this.searchResult?.showPriceSort;
    }

    private get searchTranslation(): SearchTranslationViewObject | null {
        return this.searchResult?.translation ?? null;
    }

    private get explanation(): string | null {
        return this.searchResult?.explanation ?? null;
    }

    private firstTime = true;
    private facetPickerActive = false;

    private setFacetPickerState(state: boolean) {
        this.facetPickerActive = state;
    }

    private totalResultsTxt(totalResults: number): string {
        return this.typing || this.searching ? '...' : localeNumberFilter(totalResults).toString();
    }

    private termSearchLink(term: string): RawLocation {
        return urlHelper.getLocationForTerm(term);
    }

    private get trackingListName(): string {
        const baseName = this.contextKey.toLowerCase();
        return `${baseName}-${this.$route.name}-${(this.activeMenu?.id && `sub:${this.activeMenu.id}`) || 'base'}`;
    }

    private get hasItemSelectionCode(): boolean {
        return this.searchResult?.hasItemSelectionCode ?? false;
    }

    private prevPageId: number = this.pageId;

    @Watch('searching')
    private onSearchStateChange(searching: boolean, searched: boolean) {
        if (!this.firstTime && searching && !searched) {
            this.prevPageId = this.pageId;
        } else if (this.pageId !== 1 && this.prevPageId !== this.pageId && !searching && searched) {
            if (!this.firstTime && !this.facetPickerActive) {
                this.$nextTick(() => scrollService.animateScroll(0, 400, this.scrollContainer));
            }
        }
        this.firstTime = false;
    }

    private get isStaff(): boolean {
        return permissionService.isStaff();
    }

    get noResultProductRecommendationBlockContent(): Partial<ProductRecommendationsBlockViewModel> {
        return {
            productCount: 18,
            displayLocation: 'SearchOverlay',
            appendToPreviousBlock: false,
            headline: dictionaryService.get('Search.TermWithNoResult'),
            allowFillIfNecessary: true,
            hasImages: false,
            showFeedProducts: false,
            stockFilter: ProductRecommendationStockFilterType.Both,
            productBackgroundColor: 'white'
        };
    }
}
